import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorLogsFilterHeader from "../ErrorLogsFilterHeader";
import { ErrorLogExpandedRow, renderErrorCell } from "./ErrorLogExpandedRow";
import Pagination from "components/Pagination";
import Button from "components/Button";
import {
  trackAnalyticsActionClicked,
  trackAnalyticsSubpageViewed,
} from "utils/Tracking";
import { useAnalyticsFilters } from "providers/AnalyticsProvider/AnalyticsFilterProvider";
import { useOverviewData } from "providers/AnalyticsProvider/OverviewDataProvider";
import { useErrorLogs } from "providers/AnalyticsProvider/ErrorLogsProvider";
import ErrorLogsBulkActionsBar from "./components/ErrorLogsBulkActionsBar";
import { useAnalyticsDownload, useModalNavigation } from "hooks/analytics";
import { OVERVIEW_PAGE_SIZE } from "utils/analytics/constants";
import { buildErrorLogsDownloadParams } from "utils/analytics/filters";
import { Column, DataTable } from "components/Table";
import { TABLE_CONFIGS } from "utils/analytics/tableConfigs";
import EmptyTableState from "../shared/components/EmptyTableState";
import RowActionsMenu from "../shared/components/RowActionsMenu";
import { ErrorLogRow } from "utils/analytics";
import { ChevronDownIcon } from "@heroicons/react/outline";

interface ErrorLogsTableProps {
  mode: "overview" | "full";
}

interface RowSelectionConfig {
  onSelectAll: () => void;
  onRowSelect: (itemId: string) => void;
  onRowHide: (itemId: string) => void;
}

interface ErrorLogsDataTableProps {
  rows: ErrorLogRow[];
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
  rowSelection?: RowSelectionConfig;
}

const ErrorLogsDataTable = ({ rows, isLoading, error, onRetry, rowSelection }: ErrorLogsDataTableProps) => {
  const config = TABLE_CONFIGS.error;
  const { selectedErrorColumns } = useAnalyticsFilters();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Reset during render (child effects run first). Keyed on ids + isLoading: ids catch
  // cached page swaps, isLoading catches same-id refetches; selection toggles do neither.
  const resetKey = `${isLoading}|${rows.map((row) => row.request_id).join(",")}`;
  const [seenKey, setSeenKey] = useState(resetKey);
  if (resetKey !== seenKey) {
    setSeenKey(resetKey);
    setExpandedIds(new Set());
  }

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const visibleHeaders = selectedErrorColumns?.length
    ? config.headers.filter((h) => selectedErrorColumns.includes(h.key))
    : config.headers;

  const allSelected = rows.length > 0 && rows.every((row) => row.isSelected);

  const selectColumn: Column<ErrorLogRow> = {
    key: "select",
    header: (
      <input
        type="checkbox"
        checked={allSelected}
        onChange={rowSelection?.onSelectAll}
        className="w-4 h-4 text-rebrandBlue-600 border-gray-300 rounded focus:ring-rebrandBlue-500"
      />
    ),
    thClassName: "w-9 !px-2 text-center",
    cellClassName: "!px-2 text-center",
    cell: (row) => (
      <input
        type="checkbox"
        checked={!!row.isSelected}
        onChange={() => rowSelection?.onRowSelect(row.request_id)}
        onClick={(e) => e.stopPropagation()}
        className="w-4 h-4 border-gray-300 rounded focus:ring-rebrandIndigo-500 text-rebrandIndigo-600 cursor-pointer"
        title={row.isHidden ? "Select hidden item" : "Select item"}
      />
    ),
  };

  const actionsColumn: Column<ErrorLogRow> = {
    key: "actions",
    header: "",
    thClassName: "w-12 !px-2",
    cellClassName: "!px-2",
    cell: (row) => (
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <RowActionsMenu
          onHideRow={() => rowSelection?.onRowHide(row.request_id)}
          isHidden={row.isHidden}
        />
      </div>
    ),
  };

  const columns: Column<ErrorLogRow>[] = [
    ...(rowSelection ? [selectColumn] : []),
    {
      key: "expand",
      header: "",
      thClassName: "w-9 !px-2",
      cellClassName: "!px-2",
      cell: (row) => (
        <ChevronDownIcon
          className={`w-5 h-5 mx-auto transition-transform ${
            expandedIds.has(row.request_id) ? "rotate-180" : ""
          }`}
        />
      ),
    },
    ...visibleHeaders.map(
      (header): Column<ErrorLogRow> => ({
        key: header.key,
        header: header.label,
        tooltip: header.tooltip,
        cell: (row) => renderErrorCell(row, header.key),
      })
    ),
    ...(rowSelection ? [actionsColumn] : []),
  ];

  return (
    <DataTable
      columns={columns}
      rows={error ? [] : rows}
      getRowKey={(row) => row.request_id}
      isLoading={isLoading}
      onRowClick={(row) => toggleExpanded(row.request_id)}
      className="min-w-[900px]"
      rowClassName={(row) => (row.isHidden ? "opacity-50" : undefined)}
      empty={<EmptyTableState type="error" error={error} onRetry={onRetry} />}
      renderExpandedRow={(row) =>
        expandedIds.has(row.request_id) ? <ErrorLogExpandedRow item={row} /> : null
      }
    />
  );
};

const OverviewErrorLogsTable = () => {
  const navigate = useNavigate();
  const config = TABLE_CONFIGS.error;
  const { overviewErrorLogs, isOverviewLoading, error, refetchOverview } = useOverviewData();

  const handleViewAll = () => {
    trackAnalyticsSubpageViewed({ section: "error_logs" });
    navigate(config.route);
  };

  return (
    <div className="flex gap-2 flex-col">
      <ErrorLogsDataTable
        rows={overviewErrorLogs}
        isLoading={isOverviewLoading}
        error={!!error}
        onRetry={refetchOverview}
      />
      {!error && overviewErrorLogs.length >= OVERVIEW_PAGE_SIZE && (
        <div className="mt-4">
          <Button
            fullWidth
            text={config.viewAllButtonText}
            onClick={handleViewAll}
            className="button button-secondary"
          />
        </div>
      )}
    </div>
  );
};

const FullErrorLogsTable = () => {
  const config = TABLE_CONFIGS.error;
  const { dateRange, errorLogsFilters } = useAnalyticsFilters();
  const {
    errorLogsData,
    setErrorLogsData,
    loading,
    goToPage,
    hasNextPage,
    errorLogsError,
    retryErrorLogsPage,
    setSearchTerm,
    showHidden,
    setShowHidden,
    setHidden,
    setHiddenBatch,
  } = useErrorLogs();

  const { navigateToModal } = useModalNavigation();
  const { download, isDownloading } = useAnalyticsDownload();

  // --- Selection logic (UI-only, not data-fetching) ---

  const handleRowSelect = (itemId: string) => {
    setErrorLogsData((prev) => ({
      ...prev,
      list: prev.list.map((item) =>
        item.request_id === itemId ? { ...item, isSelected: !item.isSelected } : item
      ),
    }));
  };

  const handleSelectAll = () => {
    setErrorLogsData((prev) => {
      const selectableItems = showHidden
        ? prev.list
        : prev.list.filter((item) => !item.isHidden);

      const allSelected = selectableItems.every((item) => item.isSelected);

      return {
        ...prev,
        list: prev.list.map((item) => {
          const isSelectable = showHidden || !item.isHidden;
          return isSelectable ? { ...item, isSelected: !allSelected } : item;
        }),
      };
    });
  };

  const handleRowHide = (itemId: string) => {
    const current = errorLogsData.list.find((item) => item.request_id === itemId);
    setHidden(itemId, !current?.isHidden);
  };

  const selectedIds = errorLogsData.list.filter((item) => item.isSelected).map((item) => item.request_id);

  const clearSelection = () => {
    setErrorLogsData((prev) => ({
      ...prev,
      list: prev.list.map((item) => ({ ...item, isSelected: false })),
    }));
  };

  // Clear ticks on toggle so the bulk bar never acts on hidden rows the user can no longer see
  const handleHiddenToggle = (show: boolean) => {
    setShowHidden(show);
    clearSelection();
  };

  const hasHiddenSelected = errorLogsData.list.some((item) => item.isSelected && item.isHidden);

  // --- Table data (filtered by hidden state) ---

  const data = showHidden ? errorLogsData.list : errorLogsData.list.filter((item) => !item.isHidden);

  // --- Action handlers ---

  const handleFiltersClick = () => {
    navigateToModal("/analytics/error-logs/filter");
    trackAnalyticsActionClicked({
      section: "error_logs",
      action: "open_filters",
      target: "error_logs_table",
    });
  };

  const handleCustomizeColumns = () => {
    navigateToModal("/analytics/error-logs/customize-columns", {
      tableHeaders: config.headers,
      tableType: "error",
    });
  };

  const handleDownload = () => {
    // No global filters: this page's table ignores them, and the export must
    // match the table the user is looking at
    download({
      dateRange,
      filenamePrefix: "error-logs",
      type: "error-logs",
      extraParams: buildErrorLogsDownloadParams(errorLogsFilters),
      trackingEvent: "error_logs",
    });
  };

  return (
    <>
      <ErrorLogsFilterHeader
        onFiltersClick={handleFiltersClick}
        searchValue={errorLogsFilters.searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by id"
        showHidden={showHidden}
        onHiddenToggle={handleHiddenToggle}
        onCustomizeColumns={handleCustomizeColumns}
        onDownload={handleDownload}
        downloadButtonText="DOWNLOAD LOGS"
        downloadLoading={isDownloading}
        disableDownload={isDownloading}
        downloadLoadingText="DOWNLOADING..."
      />
      <div className="flex gap-2 flex-col">
        <ErrorLogsDataTable
          rows={data}
          isLoading={loading}
          error={!!errorLogsError}
          onRetry={retryErrorLogsPage}
          rowSelection={{
            onRowSelect: handleRowSelect,
            onRowHide: handleRowHide,
            onSelectAll: handleSelectAll,
          }}
        />

        {/* Past page 1, pagination stays visible even on error so cached
            earlier pages remain reachable */}
        {(errorLogsData.currentPage > 1 || (hasNextPage && !errorLogsError)) && (
          <Pagination
            currentPage={errorLogsData.currentPage}
            onPageChange={goToPage}
            hasNextPage={hasNextPage}
            onTrack={(action) =>
              trackAnalyticsActionClicked({ section: "global_all", action, target: "overview_tables" })
            }
          />
        )}
      </div>

      <ErrorLogsBulkActionsBar
        selectedCount={selectedIds.length}
        onHide={() => setHiddenBatch(selectedIds, true)}
        onUnhide={() => setHiddenBatch(selectedIds, false)}
        onClose={clearSelection}
        onSelectAll={handleSelectAll}
        hasHiddenSelected={hasHiddenSelected}
      />
    </>
  );
};

const ErrorLogsTable = ({ mode }: ErrorLogsTableProps) => {
  if (mode === "overview") return <OverviewErrorLogsTable />;
  return <FullErrorLogsTable />;
};

export default ErrorLogsTable;
